import { observer } from "mobx-react-lite";
import { FunctionComponent } from "react";
import { Outlet } from "react-router-dom";

import {
  dataAttribute,
  DataAttributes,
  PrimaryButton,
  Stack,
  Tile
} from "@bps/fluent-ui";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { ContactType } from "@libs/gateways/practice/PracticeGateway.dtos.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { usePatientLabel } from "@ui-components/hooks/usePatientLabel.ts";

import { Labels } from "../shared-components/types/labels.enums.types.ts";
import { PeopleScreenPivot } from "./components/PeopleScreenPivot.tsx";

const PeopleScreenBase: FunctionComponent = observer(() => {
  const {
    core,
    practice: {
      ui: { showAddContact }
    }
  } = useStores();

  const patientLabel = usePatientLabel();

  const onAddContact = (type: ContactType) => () => {
    showAddContact(type);
  };

  const canOpenRecord = core.hasPermissions([
    Permission.ContactRead,
    Permission.ContactWrite,
    Permission.PatientRead,
    Permission.PatientWrite
  ]);

  return (
    <Stack
      grow
      styles={{
        root: {
          height: "100%",
          maxWidth: 1500,
          margin: "0 auto"
        }
      }}
    >
      <Stack
        horizontal
        horizontalAlign="space-between"
        styles={{ root: { padding: "4px 0 12px " } }}
      >
        <PeopleScreenPivot />
        {canOpenRecord && (
          <Stack verticalAlign="end">
            <PrimaryButton
              split
              onClick={onAddContact(ContactType.Patient)}
              iconProps={{ iconName: "Add" }}
              text={`New ${patientLabel}`}
              menuProps={{
                items: [
                  {
                    key: "addContact",
                    onClick: onAddContact(ContactType.Individual),
                    text: `New non-${patientLabel}`
                  },
                  {
                    key: "addOrganisation",
                    onClick: onAddContact(ContactType.Organisation),
                    text: Labels.newOrganisation
                  }
                ]
              }}
              {...dataAttribute(DataAttributes.Element, "btn-add-contact")}
            />
          </Stack>
        )}
      </Stack>
      <Tile styles={{ root: { height: "100%" } }}>
        <Stack verticalFill horizontal tokens={{ childrenGap: 48 }}>
          <Stack
            grow
            styles={{
              root: {
                height: "100%"
              }
            }}
          >
            <Outlet />
          </Stack>
        </Stack>
      </Tile>
    </Stack>
  );
});

const PeopleScreen = withFetch(
  x => [x.practice.ref.contactStatuses.load()],
  PeopleScreenBase
);

PeopleScreen.displayName = "PeopleScreen";
// ⚠ It should be exported as default since it is used for React.lazy
// eslint-disable-next-line import/no-default-export
export default PeopleScreen;
